import Module from '@/store/resource/common/module';
import Views from '@/store/resource/views';
import api from '@/factories/api';
import isEmpty from 'lodash/isEmpty';
import { normalizeGroupSort } from '@/views/contacts/boards/boardColumns';

let module = new Module(new Views());

module.add('state', {
  contactCounts: {},
  selectedOptionalFilters: {},
});

module.add('getters', {
  contactCount(state) {
    return (viewId) => {
      if (!viewId || !(viewId in state.contactCounts)) return undefined;
      return state.contactCounts[viewId];
    };
  },

  byTeam(state) {
    return (teamId) => {
      if (!teamId) return state.all;
      return state.all.filter((view) => {
        return (
          !view.teams || view.teams.length === 0 || view.teams.includes(teamId)
        );
      });
    };
  },

  byLayout(state) {
    return (layout) => {
      return state.all.filter((view) => view.layout === layout);
    };
  },

  sorted(state) {
    return [...state.all].sort((a, b) => {
      return (a.order || 0) - (b.order || 0);
    });
  },

  selectedOptionalFilter(state) {
    return (viewId) => {
      if (!viewId || !(viewId in state.selectedOptionalFilters)) return null;
      return state.selectedOptionalFilters[viewId];
    };
  },
});

module.add('mutations', {
  setContactCount(state, { viewId, count }) {
    state.contactCounts = { ...state.contactCounts, [viewId]: count };
  },

  invalidateContactCount(state, viewId) {
    const { [viewId]: _, ...rest } = state.contactCounts;
    state.contactCounts = rest;
  },

  setSelectedOptionalFilter(state, { viewId, filterIndex }) {
    state.selectedOptionalFilters = {
      ...state.selectedOptionalFilters,
      [viewId]: filterIndex,
    };
  },

  clearSelectedOptionalFilter(state, viewId) {
    const { [viewId]: _, ...rest } = state.selectedOptionalFilters;
    state.selectedOptionalFilters = rest;
  },
});

module.add('actions', {
  /**
   * Save column order for one view. Sends record ids and, when a drag
   * switched the mode, group_sort. Label, filters, and fields stay as they are.
   */
  async saveGroupOrder({ state, commit }, { id, groupOrder, groupSort }) {
    if (!id || id === 'preview') return null;

    const payload = {
      group_order: Array.isArray(groupOrder) ? groupOrder : [],
    };
    if (groupSort && typeof groupSort === 'object') {
      payload.group_sort = normalizeGroupSort(groupSort, payload.group_order);
    }

    const res = await state.resource.update(String(id), payload);
    const data = res?.data ?? null;
    const existing = (state.all || []).find(
      (view) => String(view.id) === String(id)
    );
    if (existing) {
      const update = { group_order: payload.group_order };
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        if (Array.isArray(data.group_order)) {
          update.group_order = data.group_order;
        }
        if (data.group_sort && typeof data.group_sort === 'object') {
          update.group_sort = data.group_sort;
        }
      } else if (payload.group_sort) {
        update.group_sort = payload.group_sort;
      }
      commit('update', { model: existing, update });
    }
    return data;
  },

  async loadContactCount({ commit, state }, view) {
    if (!view || !view.id) return;
    if (view.id in state.contactCounts) return;

    const filters = view.filters;
    const params = { format: 'v2', page: 1 };

    if (filters && !isEmpty(filters)) {
      params.filters = filters;
    }

    try {
      const res = await api().get('contacts', { params });
      const count = res?.count || (res?.results || []).length;
      commit('setContactCount', { viewId: view.id, count });
    } catch (err) {
      commit('setContactCount', { viewId: view.id, count: 0 });
    }
  },
});

export default module.create();
